import { ChangeDetectionStrategy, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ICropImageDialogData, ICropImageDialogResult, ILoadedImageData } from '../crop-image';
import {
    CropperPosition,
    ImageCroppedEvent,
    ImageCropperComponent,
    ImageTransform,
    LoadedImage,
} from 'ngx-image-cropper';
import { BehaviorSubject } from 'rxjs';
import { isDefined } from '@fi-shared/utils';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import * as piexif from 'piexifjs';

enum FiOrientation {
    Degrees0 = 0,
    Degrees90 = 1,
    Degrees180 = 2,
    Degrees270 = 3,
}

@Component({
    selector: 'fi-crop-image-dialog',
    template: `
        @if (!(showCropper$ | async)) {
          <fi-loading class="small"></fi-loading>
        }
        
        <div [hidden]="!(showCropper$ | async)">
          <div class="fi-crop-image-area-select-container" #areaSelectContainer>
            <image-cropper
              #imageCropperComponent
              [imageURL]="data.imgUrl"
              [canvasRotation]="canvasRotation"
              [format]="'jpeg'"
              [style.display]="(showCropper$ | async) ? null : 'none'"
              [cropper]="cropperPosition$ | async"
              (imageCropped)="imageCropped($event)"
              (imageLoaded)="imageLoaded($event)"
              (cropperReady)="cropperReady()"
              >
            </image-cropper>
          </div>
          <div class="fi-crop-image-rotate-buttons-container">
            <button mat-button (click)="rotateLeft()">
              <span class="fas fa-undo-alt"></span>
            </button>
            <button mat-button (click)="rotateRight()">
              <span class="fas fa-redo-alt"></span>
            </button>
          </div>
        </div>
        `,
    styleUrls: ['./crop-image-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-crop-image-dialog' },
    standalone: false,
})
export class CropImageDialogComponent
    extends FiDialogsContentBase<ICropImageDialogData, ICropImageDialogResult>
    implements OnInit
{
    canvasRotation = 0;
    transform: ImageTransform = {};
    showCropper$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    croppedImage: string = '';
    cropperPosition$: BehaviorSubject<CropperPosition> = new BehaviorSubject({
        x1: 0,
        x2: 0,
        y1: 0,
        y2: 0,
    });
    @ViewChild('imageCropperComponent') imageCropperComponent: ImageCropperComponent;
    private imageData: ILoadedImageData;
    private readonly maxSideLength = 300;

    ngOnInit(): void {
        // fix 'load' gots invalid file data at that.load (piexif.js:90:23)
        // for gif images
        if (this.data.imageData && this.data.imageData.indexOf('data:image/gif;base64')) return;

        const exifData = piexif.load(this.data.imageData);
        if (exifData && exifData.Exif) {
            let userComment = exifData.Exif[piexif.ExifIFD.UserComment] as string;
            if (userComment) {
                userComment = userComment.replace('UNICODE\x00\x00', '');
                const canvasRotation = parseInt(userComment);
                if (canvasRotation > FiOrientation.Degrees0 && canvasRotation <= FiOrientation.Degrees270) {
                    this.canvasRotation = canvasRotation;
                }
            }
        }
    }

    public imageCropped(event: ImageCroppedEvent): void {
        if (event.base64) {
            this.croppedImage = event.base64;
        } else if (event.blob) {
            const reader = new FileReader();
            reader.readAsDataURL(event.blob);
            reader.onloadend = () => {
                this.croppedImage = reader.result as string;
            };
        }
    }

    private setOrientationData(base64: string, canvasOrientation: number) {
        const zeroth = {};
        const exif = {};
        const gps = {};
        exif[piexif.ExifIFD.UserComment] = canvasOrientation;
        //zeroth[piexif.ImageIFD.Orientation] = FiOrientation.Degrees0 + canvasOrientation;
        const exifObj = { '0th': zeroth, Exif: exif, GPS: gps };
        const exifStr = piexif.dump(exifObj);

        base64 = piexif.insert(exifStr, base64);

        return base64;
    }

    public rotateLeft(): void {
        this.canvasRotation--;
        if (this.canvasRotation < 0) {
            this.canvasRotation += 4;
        }
        this.flipAfterRotate();
    }

    public rotateRight(): void {
        this.canvasRotation++;
        if (this.canvasRotation > 3) {
            this.canvasRotation -= 4;
        }
        this.flipAfterRotate();
    }

    public imageLoaded(image: LoadedImage): void {
        this.imageData = {
            height: image.original.size.height,
            width: image.original.size.width,
        };
        this.setInitialSelection();
        this.showCropper$.next(true);
    }

    public cropperReady(): void {
        //this.setInitialSelection();
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public getResult(): ICropImageDialogResult {
        return {
            cropImageData: this.setOrientationData(this.croppedImage, this.canvasRotation),
            selection: {
                x: this.cropperPosition$.value.x1,
                y: this.cropperPosition$.value.y1,
                height: this.cropperPosition$.value.y2,
                width: this.cropperPosition$.value.x2,
            },
        };
    }

    private setInitialSelection(): void {
        if (!isDefined(this.data.selection)) {
            //const width: number = this.imageCropperComponent.maxSize.width;
            //const height: number = this.imageCropperComponent.maxSize.height;
            const width = this.imageData ? this.imageData.width : 100;
            const height = this.imageData ? this.imageData.height : 100;
            const minSideLength: number = Math.min(width, height);
            this.cropperPosition$.next({
                x1: minSideLength < width ? (width - minSideLength) / 2 : 0,
                x2: (minSideLength < width ? (width - minSideLength) / 2 : 0) + minSideLength,
                y1: minSideLength < height ? (height - minSideLength) / 2 : 0,
                y2: minSideLength + (minSideLength < height ? (height - minSideLength) / 2 : 0),
            });
            return;
        }

        this.cropperPosition$.next({
            x1: isDefined(this.data.selection) ? this.data.selection.x : this.maxSideLength / 6,
            x2: isDefined(this.data.selection)
                ? this.data.selection.width
                : this.maxSideLength / 2 + this.maxSideLength / 4,
            y1: isDefined(this.data.selection) ? this.data.selection.y : this.maxSideLength / 4,
            y2: isDefined(this.data.selection)
                ? this.data.selection.height
                : this.maxSideLength / 2 + this.maxSideLength / 4,
        });
    }

    private flipAfterRotate(): void {
        const flippedH: boolean = this.transform.flipH;
        const flippedV: boolean = this.transform.flipV;
        this.transform = {
            ...this.transform,
            flipH: flippedV,
            flipV: flippedH,
        };
    }
}
